import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  MenuItem,
  Container,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
} from "@mui/material";

import TabletIcon from "../../../../Assets/Icons/tablet-icon.png";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../Utils";
import { isEmptyObject } from "../../../../Utils/utils";
import { useDevices } from "../../../../Hooks/useDevices";

import FieldInstructions from "./FieldInstructions";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AddDevice() {
  const [tabIndex, setTabIndex] = useState(0);

  const [deviceInfo, setDeviceInfo] = useState({
    name: "",
    imei: "",
    type: "",
    assignedUserId: "",
    isDeviceEnabled: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { id } = useParams(); // For edit mode

  const { pickers } = useDevices();

  const location = useLocation();
  const { device } = location.state || {};
  const hasPDevice = !isEmptyObject(device);

  const activeStatus = location.state?.activeStatus || "All";

  const [mode, setMode] = useState(hasPDevice ? "edit" : "add");

  const navigate = useNavigate();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  useEffect(() => {
    if (mode === "edit") {
      const {
        device_name,
        device_id,
        device_type,
        assigned_user_id,
        device_status,
      } = device;

      setDeviceInfo({
        name: device_name,
        imei: device_id,
        type: device_type,
        assignedUserId: assigned_user_id,
        isDeviceEnabled: device_status,
      });
      setMode("edit");
    } else {
      setMode("add");
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleDeviceInfoChange = (event) => {
    const { name, value } = event.target;

    setDeviceInfo((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleDeviceDisableChange = (event) => {
    setDeviceInfo((prevData) => ({
      ...prevData,
      isDeviceEnabled: event.target.checked,
    }));
  };

  const validateForm = () => {
    const rules = [
      { condition: !deviceInfo.name, message: "Device Name is required" },
      { condition: !deviceInfo.imei, message: "Device ID is required" },
    ];

    for (const rule of rules) {
      if (rule.condition) {
        showNotification("error", rule.message);
        return false;
      }
    }
    return true;
  };

  const getFormattedData = (data) => {
    const formData = new FormData();

    const dataMap = {
      device_name: data.name,
      device_id: data.imei,
      assigned_user_id: data.assignedUserId,
      device_status: data.isDeviceEnabled ? 1 : 0,
    };

    Object.entries(dataMap).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return formData;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    const endpoint =
      mode === "edit" ? `devices/update/${device.id}` : "/devices/add";

    try {
      setIsSubmitting(true);
      const deviceData = getFormattedData(deviceInfo);

      const res = await httpclient.post(endpoint, deviceData);

      const { data } = res;

      if (data.status === 200) {
        showNotification("success", data.message);

        // Navigate back to the role-specific path
        const statusPath =
          activeStatus === "All"
            ? "/devices/all"
            : `/devices/${activeStatus.toLowerCase().replace(/ /g, "-")}`;

        navigate(statusPath);

        navigate("/devices/all");
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      handleApiError(error, setIsSubmitting);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setDeviceInfo({
      name: "",
      imei: "",
      employee: "",
      isDeviceEnabled: false,
    });

    setTimeout(() => {
      // Navigate back to the role-specific path
      const statusPath =
        activeStatus === "All"
          ? "/devices/all"
          : `/devices/${activeStatus.toLowerCase().replace(/ /g, "-")}`;

      navigate(statusPath);
    }, 100);
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ overflow: "hidden" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            aria-label="device detail tabs"
          >
            <Tab
              icon={<img src={TabletIcon} alt="Tablet icon" />}
              iconPosition="start"
              label=" Device Detail"
              id="tab-0"
              aria-controls="tabpanel-0"
              sx={{
                "&.Mui-selected": { color: "#7152F3" },
              }}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabIndex} index={0}>
          <Box component="form" onSubmit={handleSave} noValidate>
            <Typography
              component="div"
              color="secondary"
              variant="caption"
              sx={{ my: 2, fontStyle: "italic" }}
            >
              Fields marked * are required
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="name"
                  label="Device Name"
                  name="name"
                  value={deviceInfo.name}
                  onChange={handleDeviceInfoChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="imei"
                  label="Device ID"
                  name="imei"
                  value={deviceInfo.imei}
                  onChange={handleDeviceInfoChange}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <FieldInstructions
                        instructions={[
                          "Swipe up from bottom of your home screen to open the App Drawer",
                          "Scroll though the apps and look for the Setting app, and tap to open it",
                          "Keep scrolling down the list until you find About Phone. Then tap on it",
                          "Scroll down to view the Device ID value that you can use",
                        ]}
                      />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  id="assignedUserId"
                  label="Assigned User"
                  name="assignedUserId"
                  value={deviceInfo?.assignedUserId}
                  onChange={handleDeviceInfoChange}
                  variant="outlined"
                >
                  <MenuItem value="">
                    <em>Assigned User</em>
                  </MenuItem>
                  {pickers &&
                    pickers.map((picker) => (
                      <MenuItem key={picker.id} value={picker.id}>
                        {picker.full_name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={deviceInfo.isDeviceEnabled}
                      onChange={handleDeviceDisableChange}
                      name="isDeviceEnabled"
                    />
                  }
                  label={
                    deviceInfo.isDeviceEnabled
                      ? "The Device is Enabled"
                      : "The Device is Disabled"
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ mr: 2, textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ backgroundColor: "#7152F3", textTransform: "none" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px",
                        marginLeft: "-12px",
                      }}
                    />
                  )}
                  {mode === "edit" ? "Update" : "Save"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
}

export default AddDevice;
